import {
    useStyles,
    SearchBar as AriaSearchBar,
    useAppDisplayModeContext,
    pxArrayToRem,
    Button,
    pxToRem,
} from '@archipro-design/aria';

import { SearchLine, CloseLine } from '@archipro-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useBoolean, useDebounce } from 'ahooks';
import * as S from './InPageSearchBar.style';
import { AnimatePresence, motion } from 'framer-motion';

const SEARCH_MIN_CHARS = 2;

interface SearchBarProps {
    hidden?: boolean;
    onSearchChange: (keyword: string) => void;
    onSearchClick?: (keyword: string) => void;
    defaultKeyword?: string;
    toggleSearchDrawer?: () => void;
    minChars?: number;
    isSearchOnSubmit?: boolean;
    preventAutoFocus?: boolean;
}

export const InPageSearchBar: React.FC<SearchBarProps> = ({
    hidden = false,
    defaultKeyword = '',
    onSearchChange,
    onSearchClick,
    toggleSearchDrawer,
    minChars = SEARCH_MIN_CHARS,
    isSearchOnSubmit = false,
    preventAutoFocus,
}) => {
    const [keyword, setKeyword] = useState(defaultKeyword);
    const { desktop } = useAppDisplayModeContext();
    const inputRef = useRef<HTMLInputElement>(null);
    const [focus, { set: setFocus }] = useBoolean(
        !!inputRef.current?.value?.length,
    );

    const { css } = useStyles({
        isEmptyKeyword: keyword === '',
        isFocus: focus,
    });

    const debouncedKeyword = useDebounce(keyword, { wait: 600 });
    const previousKeywordRef = useRef(debouncedKeyword);

    useEffect(() => {
        if (!preventAutoFocus && inputRef.current?.value?.length) {
            inputRef.current.focus();
        }
    }, [preventAutoFocus]);

    useEffect(() => {
        if (
            previousKeywordRef.current !== debouncedKeyword &&
            debouncedKeyword.length >= minChars
        ) {
            if (!isSearchOnSubmit) onSearchChange(debouncedKeyword);
            previousKeywordRef.current = debouncedKeyword;
        }
    }, [debouncedKeyword, onSearchChange, minChars, isSearchOnSubmit]);

    const onChangeKeyword = (value: string) => {
        setKeyword(value);
    };

    const onClose = () => {
        setKeyword('');
        onSearchChange('');
        if (!desktop) {
            inputRef.current?.focus();
        }
    };

    const searchIconSize = desktop ? 24 : 24;

    return hidden ? null : (
        <div className={css(desktop ? S.SearchBarDesktop : S.SearchBarMobile)}>
            <AriaSearchBar
                iconButtonAfter={{
                    onClick: () => {
                        if (keyword) {
                            onClose();
                        }
                        inputRef.current?.focus();
                    },
                    size: searchIconSize,
                    padding: 0,
                    icon: keyword ? (
                        <CloseLine />
                    ) : !focus ? (
                        <SearchLine />
                    ) : undefined,
                }}
                iconButtonBefore={undefined}
                searchInput={{
                    type: 'search',
                    input: {
                        value: keyword,
                        autoFocus: !desktop && !keyword && true,
                        ref: inputRef,
                    },
                    onChange: (e: React.SyntheticEvent) =>
                        onChangeKeyword((e.target as HTMLInputElement).value),
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                            e.currentTarget.blur();
                            if (isSearchOnSubmit)
                                onSearchChange(debouncedKeyword);
                        }
                    },
                    onClick: (e: React.SyntheticEvent) =>
                        onSearchClick &&
                        onSearchClick((e.target as HTMLInputElement).value),
                    onFocus: () => setFocus(true),
                    onBlur: () => setFocus(false),
                }}
                variables={{
                    searchBarPadding: !desktop
                        ? pxArrayToRem([14, 8])
                        : undefined,
                    searchInputPaddingX: !desktop ? '0' : undefined,
                    searchBarBorderRadius: !desktop ? '4px' : undefined,
                }}
            />
            {desktop && <div className={'animated-underline'}></div>}
            {!desktop && (
                <AnimatePresence>
                    {focus && (
                        <motion.div
                            style={{ overflow: 'hidden' }}
                            initial={{ width: 0 }}
                            animate={{ width: 'auto' }}
                            exit={{ width: 0 }}
                        >
                            <Button
                                variables={() => ({
                                    buttonPadding: pxToRem(9),
                                })}
                                size={14}
                                transparent
                                className={css(S.MobileButtonCancel)}
                                onClick={() => {
                                    toggleSearchDrawer?.();
                                }}
                            >
                                Cancel
                            </Button>
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </div>
    );
};
